"use client";

import { CTA } from "@/app/(public)/shared/components/CTA";
import "@/app/(public)/shared/tokens/colors.css";
import "@/app/(public)/shared/tokens/typography.css";
import { fetchCompetitors } from "@/lib/publications/api";
import type { CompetitorPost } from "@/lib/publications/types";
import { Suspense, useEffect, useState } from "react";
import ComparisonCard from "./_components/ComparisonCard";
import ComparisonCardSkeleton from "./_components/ComparisonCardSkeleton";
import ErrorState from "./_components/ErrorState";
import LogoStack from "./_components/LogoStack";
import LogoTile from "./_components/LogoTile";
import ThirdPartyToggle from "./_components/ThirdPartyToggle";
import VsChip from "./_components/VsChip";
import { useThirdParty } from "./_hooks/useThirdParty";
import { AGENCY_CTA, HERO_LOGO_STACK_COUNT, LOAD_ERROR_MESSAGE } from "./_logic/constants";

const SKELETON_CARDS = 6;

function CompetitorInner() {
  // Hooks
  const { isThirdParty, withMode, toggle } = useThirdParty();

  // States
  const [competitors, setCompetitors] = useState<CompetitorPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Derived values
  const listed = competitors.filter((c) => !c.unlisted).sort((a, b) => Number(b.featured) - Number(a.featured));

  // Effects
  useEffect(() => {
    fetchCompetitors()
      .then(setCompetitors)
      .catch((err) => {
        console.error("Error fetching competitors:", err);
        setError(LOAD_ERROR_MESSAGE);
      })
      .finally(() => setLoading(false));
  }, []);

  // Render
  if (error) return <ErrorState title="Couldn't load comparisons" message={error} retry />;

  return (
    <div data-section="competitor">
      <section className="container-page container-top">
        <div className="mx-auto flex max-w-3xl flex-col items-center gap-8 pb-14 text-center md:pb-20">
          <div className="flex items-center gap-3 md:gap-4">
            <LogoTile size="md" />
            <VsChip />
            {loading ? (
              <div className="h-16 w-56 animate-pulse rounded-2xl bg-gray-100 md:w-[472px]" />
            ) : (
              <LogoStack logos={listed.slice(0, HERO_LOGO_STACK_COUNT).map((c) => c.competitorLogo)} />
            )}
          </div>
          <div className="flex flex-col items-center gap-5">
            <h1 className="text-balance text-h1 text-landing-primary">How Nowadays compares</h1>
            <p className="max-w-2xl text-pretty text-body-xl text-landing-secondary">
              {isThirdParty
                ? "Side-by-side comparisons for agencies choosing a platform to run client events."
                : "Honest, side-by-side comparisons with the other event platforms, feature by feature."}
            </p>
          </div>
          <ThirdPartyToggle checked={isThirdParty} onToggle={toggle} />
        </div>
      </section>

      <section className="container-page pb-16 md:pb-24">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-6 lg:grid-cols-3">
          {loading
            ? Array.from({ length: SKELETON_CARDS }).map((_, i) => <ComparisonCardSkeleton key={i} />)
            : listed.map((c) => <ComparisonCard key={c.id} competitor={c} href={withMode(`/comparisons/${c.slug}`)} />)}
        </div>
      </section>

      <div className="container-page">
        <CTA {...(isThirdParty ? AGENCY_CTA : {})} />
      </div>
    </div>
  );
}

/**
 * Comparisons index: hero, every listed comparison as a card, and a closing CTA.
 * @returns the index page
 */
export default function Competitor() {
  return (
    <Suspense fallback={null}>
      <CompetitorInner />
    </Suspense>
  );
}
